// Stage 47: Phantom Module — frontend API client.
//
// Operator-only growth-direction harness. Mirrors frontend/src/api/homunculus.ts
// in style. NO model-facing surface; the air gap is structural — see
// PhantomModule-TINS.md §Implementation Notes #7.
//
// Helpers consume the global `apiGet`/`apiJson` from ./client which
// already attaches the Firebase ID token.

import { apiGet, apiJson, apiGetRaw } from './client';

export type DetectorType =
  | 'recurrent-recall'
  | 'closeout-zone'
  | 'calibration-drift'
  | 'bias-flag-persistence'
  | 'horizon-alignment'
  | 'wipeout-repetition';

export type StubType =
  | 'capability-gap'
  | 'redundancy'
  | 'calibration-drift'
  | 'architectural-pressure';

export type SignalSource = 'memory' | 'homunculus' | 'horizon';

export interface PhantomEvidence {
  detector: DetectorType;
  signalCount: number;
  source: SignalSource;
  excerpt: string;
  references: string[];
}

export interface PhantomScoreBreakdown {
  frequency: number;
  recency: number;
  crossSource: number;
  horizonAlignment: number;
  cost: number;
}

export interface PhantomStub {
  id: string;
  type: StubType;
  title: string;
  pressure: string;
  suggestedScope: string;
  evidence: PhantomEvidence[];
  scoreBreakdown: PhantomScoreBreakdown;
  valueScore: number;
  detectedAt: string;
  firstSeenAt: string;
}

export interface PhantomRegenInputs {
  memoryExportSize: number | null;
  homunculusExportSize: number | null;
  horizonDocsRead: number;
}

export interface PhantomRegenSummary {
  regenerationId: string;
  generatedAt: string;
  stubCount: number;
  stubs: PhantomStub[];
  inputs: PhantomRegenInputs;
  signalsFired: Record<DetectorType, number>;
  suppressedStubs: number;
  durationMs: number;
}

export interface PhantomHealth {
  ok: boolean;
  service: string;
  version: string;
  enabled: boolean;
  lastRegeneration: number | null;
  region: string;
  timestamp: string;
}

export interface PhantomReadResponse {
  regenerationId: string | null;
  generatedAt: string | number;
  structured?: unknown;
  markdown: string;
}

export interface PhantomSuppression {
  stubId: string;
  reason: string;
  suppressedAt: string;
  expiresAt: string | null;
}

export interface PhantomHistoryItem {
  regenerationId: string;
  generatedAt: string;
  stubCount: number;
  durationMs: number;
}

export interface PhantomHorizonOperatorDoc {
  id: string;
  filename: string;
  source: 'operator' | 'bundled';
  uploadedAt: string;
  bodySize: number;
}

export interface PhantomHorizonBundledDoc {
  filename: string;
  bodySize: number;
}

// ─── operations ─────────────────────────────────────────────────────

export const phantomHealthcheck = () =>
  apiGet<PhantomHealth>('/api/phantom/healthcheck');

export interface PhantomRegenerateOptions {
  siblings?: { memory?: boolean; homunculus?: boolean };
  horizonScan?: boolean;
  maxStubs?: number;
  detectorOverrides?: Record<string, { enabled?: boolean; threshold?: number }>;
}

export const phantomRegenerate = (options?: PhantomRegenerateOptions) =>
  apiJson<PhantomRegenSummary>('POST', '/api/phantom/regenerate', { options });

export const phantomReadMarkdown = async (regenerationId?: string): Promise<string> => {
  const qs = regenerationId
    ? `?regenerationId=${encodeURIComponent(regenerationId)}`
    : '';
  const res = await apiGetRaw(`/api/phantom/read${qs}`);
  return res.text();
};

export const phantomReadJson = (regenerationId?: string) => {
  const qs = regenerationId
    ? `?format=json&regenerationId=${encodeURIComponent(regenerationId)}`
    : '?format=json';
  return apiGet<PhantomReadResponse>(`/api/phantom/read${qs}`);
};

export const phantomSuppress = (input: {
  stubId: string;
  reason: string;
  expiresAt?: string;
}) =>
  apiJson<{ suppressed: boolean; stubId: string; expiresAt: string | null }>(
    'POST',
    '/api/phantom/suppress',
    input,
  );

export const phantomUnsuppress = (input: { stubId: string }) =>
  apiJson<{ unsuppressed: boolean; stubId: string }>(
    'POST',
    '/api/phantom/unsuppress',
    input,
  );

export const phantomListSuppressions = () =>
  apiGet<{ suppressions: PhantomSuppression[] }>(
    '/api/phantom/suppressions',
  );

export const phantomHistory = (limit = 20, cursor?: string) => {
  const qs = new URLSearchParams();
  qs.set('limit', String(limit));
  if (cursor) qs.set('cursor', cursor);
  return apiGet<{ regenerations: PhantomHistoryItem[]; nextCursor: string | null }>(
    `/api/phantom/history?${qs.toString()}`,
  );
};

export const phantomExportUrl = (apiBase: string): string =>
  `${apiBase}/api/phantom/export?format=ndjson&includeHistory=true&includeSuppressions=true`;

export const phantomListHorizon = () =>
  apiGet<{
    operatorDocs: PhantomHorizonOperatorDoc[];
    bundledDocs: PhantomHorizonBundledDoc[];
  }>('/api/phantom/horizon');

export const phantomUploadHorizon = (filename: string, body: string) =>
  apiJson<{ id: string; filename: string; bulletsParsed: number }>(
    'POST',
    '/api/phantom/horizon',
    { filename, body },
  );

export const phantomDeleteHorizon = (id: string) =>
  apiJson<{ deleted: boolean; id: string }>(
    'DELETE',
    `/api/phantom/horizon/${encodeURIComponent(id)}`,
  );
